<script setup lang="ts">
import { type AdminTrackableProductResponse } from "mws-fabricks-ts-sdk";
import { ref } from "vue";

import BaseButton from "@/common/components/BaseButton.vue";
import FormKitDropdown from "@/common/components/FormKitDropdown.vue";
import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import PageWrapper from "@/common/components/PageWrapper.vue";
import RouterLinkVue from "@/common/components/RouterLinkVue.vue";
import PaginatedTable from "@/common/components/table/PaginatedTable.vue";
import type { TableColumn } from "@/common/components/table/types";
import FabricksPartnerAutocomplete from "@/fabricks/partners/components/FabricksPartnerAutocomplete.vue";
import { useGetFabricksTrackableProductsListQuery } from "@/fabricks/trackableProducts/api/queries";
import TrackableProductCreationModal from "@/fabricks/trackableProducts/components/TrackableProductCreationModal.vue";
import TrackableProductsListActionMenu from "@/fabricks/trackableProducts/components/TrackableProductsListActionMenu.vue";

definePage({
  meta: {
    auth: ["FabricksAdmin.Access"],
  },
});

const columns: TableColumn<AdminTrackableProductResponse>[] = [
  {
    key: "name",
    label: "Name",
    type: "link",
    pathConstructor: (item) =>
      `/fabricks/trackable-products/${item.id}/details`,
  },
  {
    key: "partnerName",
    label: "Partner",
    type: "slot",
  },
  {
    key: "dateCreated",
    label: "Date Created",
    type: "date",
  },
  {
    key: "batchDescription",
    label: "Description",
    type: "text",
  },
  {
    key: "label",
    label: "Label",
    type: "text",
  },
  {
    key: "redirectUrl",
    label: "Landing page",
    type: "slot",
  },
  {
    key: "isLinkedToChip",
    label: "Linked To Chip",
    type: "slot",
  },
];

const breadcrumbLinks = [
  {
    label: "Trackable Products",
    to: "#",
  },
];

const isProductCreationModalOpen = ref(false);

const filterPanelKeys = [
  "partnerId",
  "batchDescription",
  "isLinkedToChip",
  "isArchived",
  "startDate",
  "endDate",
];
</script>

<template>
  <PageWrapper
    title="Trackable Products"
    data-cy="fabricks-trackable-products-list"
    :breadcrumb-links="breadcrumbLinks"
  >
    <template #actions>
      <BaseButton
        variant="primary"
        label="Create"
        data-test="create-trackable-product-button"
        @click="isProductCreationModalOpen = true"
      />
    </template>
    <PaginatedTable
      data-test="trackable-products-list"
      :collection-query-fn="useGetFabricksTrackableProductsListQuery"
      :columns="columns"
      :is-searchable="true"
      :date-filter="{
        startAfter: undefined,
        startAfterKey: 'startDate',
        endBefore: undefined,
        endBeforeKey: 'endDate',
      }"
      :filter-panel-keys="filterPanelKeys"
    >
      <template #filters="{ filters }">
        <FabricksPartnerAutocomplete
          v-model="filters.partnerId"
          placeholder="Partner"
          label=""
          name="partnerId"
          data-test="partner-quick-filter"
          :include-disabled="false"
          outer-class="!max-w-[20rem]"
        />
      </template>
      <template #panelFilters="{ filters }">
        <FabricksPartnerAutocomplete
          v-model="filters.partnerId"
          label="Partner"
          name="partnerId"
          data-test="partner-panel-filter"
          :include-disabled="false"
        />
        <FormKit
          v-model="filters.batchDescription"
          type="text"
          label="Description"
          placeholder="Search description"
          name="batchDescription"
          data-test="batch-description-panel-filter"
        />
        <FormKitDropdown
          v-model="filters.isLinkedToChip"
          label="Is Linked To Chip"
          placeholder="Select option"
          data-test="is-linked-to-chip-panel-filter"
          :options="[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]"
        />
        <FormKit
          v-model="filters.isArchived"
          type="select"
          label="Is Archived"
          data-test="is-archived-panel-filter"
          :options="[
            { label: 'No', value: false },
            { label: 'Yes', value: true },
          ]"
          :value="false"
          placeholder="Select option"
        />
      </template>
      <template #partnerName="{ item }">
        <RouterLinkVue
          v-if="item.partnerId"
          :to="`/fabricks/partners/${item.partnerId}/details`"
        >
          {{ item.partner.name }}
        </RouterLinkVue>
      </template>
      <template #redirectUrl="{ item }">
        <a
          v-if="item.redirectUrl"
          :href="item.redirectUrl"
          :is-external-link="true"
          class="text-indigo-600 hover:text-indigo-900"
        >
          {{ "View landing page " }}
          <BaseIcon
            name="heroicons:arrow-top-right-on-square"
            class="size-4"
          />
        </a>
      </template>
      <template #rowActions="{ item }">
        <TrackableProductsListActionMenu :item="item" />
      </template>
      <template #isLinkedToChip="{ item }">
        <BaseIcon
          :name="item.chips.length > 0 ? 'heroicons:check' : 'heroicons:x-mark'"
          :class="item.chips.length > 0 ? 'text-green-500' : 'text-red-500'"
          :size="21"
          class="icon-class"
        />
      </template>
    </PaginatedTable>
    <TrackableProductCreationModal
      v-model:is-open="isProductCreationModalOpen"
    />
  </PageWrapper>
</template>
